import { MarkerType } from '@xyflow/react';
import { useFlowStore } from '../store/flowStore';
import { EDGE_PATH_TYPES, type EdgePathType, type NodeKind } from '../types';

const KIND_LABELS: Record<NodeKind, string> = {
  startEnd: 'Início / Fim',
  process: 'Processo',
  decision: 'Decisão',
  inputOutput: 'Entrada / Saída',
  annotation: 'Anotação',
  group: 'Grupo',
};

const EDGE_TYPE_LABELS: Record<EdgePathType, string> = {
  smoothstep: 'Curva suave',
  bezier: 'Bézier',
  step: 'Degrau',
  straight: 'Reta',
};

export function PropertiesPanel() {
  const nodes = useFlowStore((s) => s.nodes);
  const edges = useFlowStore((s) => s.edges);
  const updateNodeData = useFlowStore((s) => s.updateNodeData);
  const updateEdge = useFlowStore((s) => s.updateEdge);
  const presentationMode = useFlowStore((s) => s.presentationMode);
  const readOnly = useFlowStore((s) => s.readOnly);

  const selectedNodes = nodes.filter((n) => n.selected);
  const selectedEdges = edges.filter((e) => e.selected);
  const node = selectedNodes[0];
  const edge = !node ? selectedEdges[0] : undefined;

  if (presentationMode || readOnly) return null;

  return (
    <section className="sidebar-section sidebar-properties">
      <h2 className="panel-title">Propriedades</h2>

      {node && (
        <div className="panel-body">
          <span className={`kind-badge badge-${node.type}`}>
            {KIND_LABELS[(node.type ?? 'process') as NodeKind]}
          </span>

          <label className="field">
            <span>Rótulo</span>
            <textarea
              rows={3}
              value={node.data.label}
              onChange={(e) =>
                updateNodeData(node.id, { label: e.target.value }, false)
              }
            />
          </label>

          <label className="field">
            <span>Cor</span>
            <input
              type="color"
              value={node.data.color ?? '#3b82f6'}
              onChange={(e) =>
                updateNodeData(node.id, { color: e.target.value }, false)
              }
            />
          </label>

          {node.type === 'decision' && (
            <p className="panel-note">
              Duplo clique em Sim/Não (ou … nas outras pontas) para editar o
              rótulo. Arraste de qualquer bolinha para criar uma nova saída.
            </p>
          )}

          <div className="field-row">
            <div className="field readonly">
              <span>Posição</span>
              <code>
                {Math.round(node.position.x)}, {Math.round(node.position.y)}
              </code>
            </div>
            <div className="field readonly">
              <span>Tamanho</span>
              <code>
                {Math.round(node.measured?.width ?? node.width ?? 0)} ×{' '}
                {Math.round(node.measured?.height ?? node.height ?? 0)}
              </code>
            </div>
          </div>

          {selectedNodes.length > 1 && (
            <p className="panel-note">
              {selectedNodes.length} nós selecionados — editando o primeiro.
            </p>
          )}
        </div>
      )}

      {edge && (
        <div className="panel-body">
          <span className="kind-badge badge-edge">Conexão</span>

          <label className="field">
            <span>Rótulo</span>
            <input
              type="text"
              value={edge.data?.label ?? ''}
              onChange={(e) =>
                updateEdge(edge.id, { data: { label: e.target.value } }, false)
              }
            />
          </label>

          <label className="field">
            <span>Tipo de linha</span>
            <select
              value={edge.data?.pathType ?? 'smoothstep'}
              onChange={(e) =>
                updateEdge(
                  edge.id,
                  { data: { pathType: e.target.value as EdgePathType } },
                  false,
                )
              }
            >
              {EDGE_PATH_TYPES.map((type) => (
                <option key={type} value={type}>
                  {EDGE_TYPE_LABELS[type]}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Cor da linha</span>
            <input
              type="color"
              value={
                typeof edge.style?.stroke === 'string'
                  ? edge.style.stroke
                  : '#000000'
              }
              onChange={(e) =>
                updateEdge(
                  edge.id,
                  {
                    style: { ...edge.style, stroke: e.target.value },
                    markerEnd: {
                      type: MarkerType.ArrowClosed,
                      color: e.target.value,
                    },
                  },
                  false,
                )
              }
            />
          </label>

          <label className="field checkbox">
            <input
              type="checkbox"
              checked={Boolean(edge.animated)}
              onChange={(e) =>
                updateEdge(edge.id, { animated: e.target.checked }, false)
              }
            />
            <span>Animada</span>
          </label>
        </div>
      )}

      {!node && !edge && (
        <div className="panel-body empty">
          <p>Selecione um nó ou uma conexão para editar suas propriedades.</p>
          <dl className="stats">
            <div>
              <dt>Nós</dt>
              <dd>{nodes.length}</dd>
            </div>
            <div>
              <dt>Conexões</dt>
              <dd>{edges.length}</dd>
            </div>
          </dl>
        </div>
      )}
    </section>
  );
}
